import uniq from "lodash/uniq";
import { shallowEqual } from "react-redux";

import type { RootState } from "~/app/store";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { GroupedCartEntry, LineItem, ResolvedGroupedCartEntry } from "~/features/cart/cart/models/CartData";
import { selectCartEntry, selectLineItem } from "~/features/cart/cart/redux/selectors/cartSelectors";
import type { ProductDescriptorSelection } from "~/features/cart/cartV2/models/ProductTableViewModel";

export interface ResolvedGroupedCartEntryV2 extends ResolvedGroupedCartEntry {
  lineItems: LineItem[];
  descriptorsByPriceReference: Record<string, ProductDescriptor>;
  mainDescriptor: ProductDescriptor | null;
  mainDescriptorSelection: ProductDescriptorSelection;
}

export function getResolvedEntryLineItems(entry: Pick<ResolvedGroupedCartEntry, "packGroups">): LineItem[] {
  return entry.packGroups.flatMap((group) => group.packEntries);
}

export function selectDescriptorForPriceReference(
  descriptors: ProductDescriptor[],
  priceReference?: string | null,
): ProductDescriptorSelection {
  if (descriptors.length === 0) {
    return {
      descriptor: null,
      strategy: "missing",
      priceReference: priceReference ?? undefined,
    };
  }

  if (descriptors.length === 1) {
    return {
      descriptor: descriptors[0],
      strategy: "single-descriptor",
      priceReference: priceReference ?? descriptors[0].priceReference,
    };
  }

  if (priceReference) {
    const matchingDescriptor = descriptors.find((descriptor) => descriptor.priceReference === priceReference);

    if (matchingDescriptor) {
      return {
        descriptor: matchingDescriptor,
        strategy: "matched-price-reference",
        priceReference,
      };
    }
  }

  const loadedDescriptor = descriptors.find((descriptor) => descriptor.isLoaded && !descriptor.isError);
  if (loadedDescriptor) {
    return {
      descriptor: loadedDescriptor,
      strategy: "first-loaded",
      priceReference: loadedDescriptor.priceReference,
    };
  }

  const nonErrorDescriptor = descriptors.find((descriptor) => !descriptor.isError);
  if (nonErrorDescriptor) {
    return {
      descriptor: nonErrorDescriptor,
      strategy: "first-non-error",
      priceReference: nonErrorDescriptor.priceReference,
    };
  }

  return {
    descriptor: descriptors[0],
    strategy: "first-available",
    priceReference: descriptors[0].priceReference,
  };
}

export function selectMainDescriptor(
  descriptors: ProductDescriptor[],
  lineItems: LineItem[],
): ProductDescriptorSelection {
  return selectDescriptorForPriceReference(descriptors, lineItems[0]?.priceReference);
}

export function selectDescriptorForLineItem(
  entry: Pick<ResolvedGroupedCartEntryV2, "descriptors" | "descriptorsByPriceReference">,
  lineItem: Pick<LineItem, "priceReference">,
): ProductDescriptorSelection {
  return selectDescriptorForPriceReference(entry.descriptors, lineItem.priceReference);
}

function compareProductDescriptorSelectionEquality(
  left: ProductDescriptorSelection,
  right: ProductDescriptorSelection,
): boolean {
  return (
    left.descriptor === right.descriptor &&
    left.strategy === right.strategy &&
    left.priceReference === right.priceReference
  );
}

export const selectResolvedGroupedEntryV2 =
  (entry: GroupedCartEntry) =>
  (state: RootState): ResolvedGroupedCartEntryV2 => {
    const resolved: ResolvedGroupedCartEntry = {
      ...entry,
      cartEntry: selectCartEntry(entry.cartEntryId)(state),
      descriptors: [],
      packGroups: entry.packGroups.map((packGroup) => ({
        ...packGroup,
        packEntries: packGroup.packEntryIds.map((id) => selectLineItem(id)(state)),
      })),
    };

    const lineItems = getResolvedEntryLineItems(resolved);
    const productCode = resolved.cartEntry.productCode;
    const priceReferences = uniq(lineItems.map((lineItem) => lineItem.priceReference ?? ""));
    const descriptors = priceReferences.map((priceReference) =>
      selectProductDescriptor(state, {
        productCode,
        priceReference,
      }),
    );
    const descriptorsByPriceReference = descriptors.reduce<Record<string, ProductDescriptor>>(
      (accumulator, descriptor) => {
        accumulator[descriptor.priceReference] = descriptor;
        return accumulator;
      },
      {},
    );
    const mainDescriptorSelection = selectMainDescriptor(descriptors, lineItems);

    return {
      ...resolved,
      descriptors,
      lineItems,
      descriptorsByPriceReference,
      mainDescriptor: mainDescriptorSelection.descriptor,
      mainDescriptorSelection,
    };
  };

export function compareResolvedGroupedEntryV2Equality(
  left: ResolvedGroupedCartEntryV2,
  right: ResolvedGroupedCartEntryV2,
): boolean {
  if (!shallowEqual(left.cartEntry, right.cartEntry)) {
    return false;
  }

  if (!shallowEqual(left.descriptors, right.descriptors)) {
    return false;
  }

  if (left.mainDescriptor !== right.mainDescriptor) {
    return false;
  }

  if (!compareProductDescriptorSelectionEquality(left.mainDescriptorSelection, right.mainDescriptorSelection)) {
    return false;
  }

  if (left.packGroups.length !== right.packGroups.length) {
    return false;
  }

  return left.packGroups.every(
    (leftPackGroup, index) =>
      !!right.packGroups[index] &&
      leftPackGroup.packEntries.length === right.packGroups[index].packEntries.length &&
      shallowEqual(leftPackGroup.packEntries, right.packGroups[index].packEntries),
  );
}
